/**
 * Admin / moderation page. Only renders for accounts with `is_admin = 1`
 * server-side; non-admins see a friendly "not authorized" message because
 * the API endpoints will 403 them anyway.
 *
 * Three views:
 *   - Players: recent accounts/guests with quick mute/ban/promote actions
 *   - Bans: current bans (player + IP), unban from here
 *   - Audit: append-only log of admin actions for accountability
 *   - Chat: recent chat across all lobbies, filterable by player
 */

import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import type { AppContext } from "../App.js";
import {
  adminListAudit,
  adminListBans,
  adminListChat,
  adminListPlayers,
  adminMe,
  adminMute,
  adminPromote,
  adminRemoveBan,
  adminBan,
} from "../lib/api.js";
import type {
  AdminAuditEntry,
  AdminBan,
  AdminChatEntry,
  AdminPlayer,
} from "../../../shared/protocol.js";

type Tab = "players" | "bans" | "chat" | "audit";

export function AdminPage() {
  const ctx = useOutletContext<AppContext>();
  const [authState, setAuthState] = useState<"loading" | "ok" | "denied">("loading");
  const [tab, setTab] = useState<Tab>("players");
  const [error, setError] = useState<string | null>(null);

  // Probe permission first so we don't flash "denied" while data loads.
  useEffect(() => {
    adminMe()
      .then((r) => setAuthState(r.isAdmin ? "ok" : "denied"))
      .catch(() => setAuthState("denied"));
  }, [ctx.me?.id]);

  if (authState === "loading") {
    return <main className="container"><p className="muted">Checking permissions…</p></main>;
  }
  if (authState === "denied") {
    return (
      <main className="container">
        <div className="card-block">
          <h2 style={{ marginTop: 0 }}>Admin</h2>
          <p className="muted">You don't have access to this page.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="container">
      {error && <div className="toast">{error}</div>}
      <div className="card-block">
        <h2 style={{ marginTop: 0 }}>Moderation</h2>
        <div className="row" style={{ gap: 8, marginBottom: 12 }}>
          <button onClick={() => setTab("players")} className={tab === "players" ? "primary" : ""}>Players</button>
          <button onClick={() => setTab("bans")} className={tab === "bans" ? "primary" : ""}>Bans</button>
          <button onClick={() => setTab("chat")} className={tab === "chat" ? "primary" : ""}>Recent chat</button>
          <button onClick={() => setTab("audit")} className={tab === "audit" ? "primary" : ""}>Audit log</button>
        </div>
        {tab === "players" && <PlayersTab onError={setError} />}
        {tab === "bans" && <BansTab onError={setError} />}
        {tab === "chat" && <ChatTab onError={setError} />}
        {tab === "audit" && <AuditTab onError={setError} />}
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Players
// ---------------------------------------------------------------------------

function PlayersTab({ onError }: { onError: (s: string | null) => void }) {
  const [players, setPlayers] = useState<AdminPlayer[]>([]);
  const [filter, setFilter] = useState("");
  const [tick, setTick] = useState(0); // bump to refetch after actions

  useEffect(() => {
    adminListPlayers(200)
      .then((r) => setPlayers(r.players))
      .catch((e) => onError(String(e)));
  }, [tick, onError]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return players;
    return players.filter(
      (p) =>
        p.displayName.toLowerCase().includes(q) ||
        (p.username ?? "").toLowerCase().includes(q) ||
        (p.lastIp ?? "").includes(q) ||
        p.id.toLowerCase().includes(q)
    );
  }, [players, filter]);

  async function withRefetch(fn: () => Promise<unknown>) {
    onError(null);
    try {
      await fn();
      setTick((t) => t + 1);
    } catch (e) {
      onError(String(e));
    }
  }

  return (
    <div>
      <input
        placeholder="Filter by name, username, IP, or id…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={{ width: "100%", marginBottom: 10 }}
      />
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Username</th>
              <th>IP</th>
              <th>Last seen</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id}>
                <td>
                  {p.displayName}
                  <div className="muted small">{p.id.slice(0, 10)}…</div>
                </td>
                <td>{p.username ? `@${p.username}` : <span className="muted">guest</span>}</td>
                <td className="mono">{p.lastIp ?? <span className="muted">—</span>}</td>
                <td>{fmtRel(p.lastSeenAt)}</td>
                <td>
                  {p.isAdmin && <span className="pill">ADMIN</span>}
                  {p.mutedUntil && p.mutedUntil > Date.now() && (
                    <span className="pill" title={new Date(p.mutedUntil).toLocaleString()}>MUTED</span>
                  )}
                </td>
                <td>
                  <div className="row" style={{ gap: 4, flexWrap: "wrap" }}>
                    <button
                      onClick={() =>
                        withRefetch(() =>
                          adminMute(p.id, p.mutedUntil && p.mutedUntil > Date.now() ? 0 : 600)
                        )
                      }
                      title={p.mutedUntil && p.mutedUntil > Date.now() ? "Unmute" : "Mute 10 minutes"}
                    >
                      {p.mutedUntil && p.mutedUntil > Date.now() ? "Unmute" : "Mute 10m"}
                    </button>
                    <button
                      className="danger"
                      disabled={p.isAdmin}
                      onClick={() => {
                        const reason = prompt(`Ban ${p.displayName}? Reason (optional):`);
                        if (reason === null) return;
                        withRefetch(() => adminBan("player", p.id, reason || undefined));
                      }}
                    >
                      Ban player
                    </button>
                    <button
                      className="danger"
                      disabled={!p.lastIp}
                      onClick={() => {
                        if (!p.lastIp) return;
                        const reason = prompt(`Ban IP ${p.lastIp}? Reason (optional):`);
                        if (reason === null) return;
                        withRefetch(() => adminBan("ip", p.lastIp!, reason || undefined));
                      }}
                    >
                      Ban IP
                    </button>
                    {p.username && (
                      <button
                        onClick={() => withRefetch(() => adminPromote(p.id, !p.isAdmin))}
                      >
                        {p.isAdmin ? "Demote" : "Promote"}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bans
// ---------------------------------------------------------------------------

function BansTab({ onError }: { onError: (s: string | null) => void }) {
  const [bans, setBans] = useState<AdminBan[]>([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    adminListBans()
      .then((r) => setBans(r.bans))
      .catch((e) => onError(String(e)));
  }, [tick, onError]);

  async function unban(kind: "player" | "ip", value: string) {
    onError(null);
    try {
      await adminRemoveBan(kind, value);
      setTick((t) => t + 1);
    } catch (e) {
      onError(String(e));
    }
  }

  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Kind</th>
            <th>Value</th>
            <th>Reason</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {bans.length === 0 && (
            <tr><td colSpan={5} className="muted" style={{ textAlign: "center", padding: 16 }}>No bans.</td></tr>
          )}
          {bans.map((b) => (
            <tr key={b.id}>
              <td><span className="pill">{b.kind}</span></td>
              <td className="mono">{b.value}</td>
              <td>{b.reason || <span className="muted">—</span>}</td>
              <td>{fmtRel(b.createdAt)}</td>
              <td>
                <button onClick={() => unban(b.kind, b.value)}>Unban</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------

function ChatTab({ onError }: { onError: (s: string | null) => void }) {
  const [entries, setEntries] = useState<AdminChatEntry[]>([]);
  const [playerId, setPlayerId] = useState("");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    adminListChat({ playerId: playerId.trim() || undefined, limit: 200 })
      .then((r) => setEntries(r.entries))
      .catch((e) => onError(String(e)));
  }, [tick, playerId, onError]);

  return (
    <div>
      <div className="row" style={{ gap: 8, marginBottom: 10 }}>
        <input
          placeholder="Filter by exact player id…"
          value={playerId}
          onChange={(e) => setPlayerId(e.target.value)}
          style={{ flex: 1 }}
        />
        <button onClick={() => setTick((t) => t + 1)}>Refresh</button>
      </div>
      <p className="muted small">
        Showing raw (un-censored) text. Buffer is per-lobby in-memory only and clears on server restart.
      </p>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>When</th>
              <th>From</th>
              <th>Lobby</th>
              <th>IP</th>
              <th>Text</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr><td colSpan={5} className="muted" style={{ textAlign: "center", padding: 16 }}>No messages.</td></tr>
            )}
            {entries.map((m, i) => (
              <tr key={i}>
                <td className="small">{fmtRel(m.at)}</td>
                <td>
                  {m.displayName}
                  <div className="muted small">{m.playerId.slice(0, 10)}…</div>
                </td>
                <td className="mono small">{m.lobbyId.slice(0, 8)}</td>
                <td className="mono small">{m.ip ?? "—"}</td>
                <td>{m.text}{m.censored && <span className="pill" style={{ marginLeft: 6 }}>CENSORED</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

function AuditTab({ onError }: { onError: (s: string | null) => void }) {
  const [entries, setEntries] = useState<AdminAuditEntry[]>([]);
  useEffect(() => {
    adminListAudit(200)
      .then((r) => setEntries(r.entries))
      .catch((e) => onError(String(e)));
  }, [onError]);
  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr>
            <th>When</th>
            <th>Action</th>
            <th>Target</th>
            <th>By</th>
            <th>Reason / detail</th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 && (
            <tr><td colSpan={5} className="muted" style={{ textAlign: "center", padding: 16 }}>No audit entries.</td></tr>
          )}
          {entries.map((e) => (
            <tr key={e.id}>
              <td className="small">{fmtRel(e.at)}</td>
              <td><span className="pill">{e.action}</span></td>
              <td className="mono small">{e.target ?? "—"}</td>
              <td className="mono small">{e.byPlayerId ?? "system"}</td>
              <td className="small">
                {e.reason ?? ""}
                {e.details && <span className="muted"> {JSON.stringify(e.details)}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function fmtRel(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  return new Date(ts).toLocaleString();
}
